function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
    ),
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true,
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this)),
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.currentTarget.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.currentTarget.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset',
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId,
    );
  }
}
customElements.define('variant-selects', VariantSelects);

class AnnouncementBar extends HTMLElement {
  constructor() {
    super();
    this.currentSlide = 0;
    this.autoRotate = this.hasAttribute('data-auto-rotate');
    this.changeSlideSpeed = this.getAttribute('change-slide-speed') || 5000;
    this.slides = this.querySelectorAll('[data-announcement-bar-slide]');
    this.autoRotateInterval = null;
  }

  connectedCallback() {
    this.autoRotate && this.startAutoRotate();
    this.addEventListener('mouseenter', this.stopAutoRotate.bind(this));
    this.addEventListener('mouseleave', this.startAutoRotate.bind(this));
  }

  disconnectedCallback() {
    this.autoRotate && this.stopAutoRotate();
    this.removeEventListener('mouseenter', this.stopAutoRotate.bind(this));
    this.removeEventListener('mouseleave', this.startAutoRotate.bind(this));
  }

  startAutoRotate() {
    this.autoRotateInterval = setInterval(this.nextSlide.bind(this), this.changeSlideSpeed);
  }

  stopAutoRotate() {
    clearInterval(this.autoRotateInterval);
  }

  previousSlide() {
    this.slides[this.currentSlide].classList.remove('--active');
    this.currentSlide = this.currentSlide === 0 ? this.slides.length - 1 : this.currentSlide - 1;
    this.slides[this.currentSlide].classList.add('--active');
  }

  nextSlide() {
    this.slides[this.currentSlide].classList.remove('--active');
    this.currentSlide = this.currentSlide === this.slides.length - 1 ? 0 : this.currentSlide + 1;
    this.slides[this.currentSlide].classList.add('--active');
  }
}
customElements.define('announcement-bar', AnnouncementBar);

class CustomLayer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this)),
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeCustomLayer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeCustomLayer(event, summaryElement) : this.openCustomLayer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openCustomLayer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeCustomLayer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeCustomLayer();
    });
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}
customElements.define('custom-layer', CustomLayer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class CardProduct extends HTMLElement {
  constructor() {
    super();
    if (this.variantSelector) {
      this.variantSelector.addEventListener('change', this.onVariantChange.bind(this));
    }
  }

  get variantSelector() {
    return this.querySelector('select');
  }
  get submitButton() {
    return this.querySelector('button[type=submit]');
  }
  get price() {
    return this.querySelector('.price');
  }
  get productForm() {
    return this.querySelector('product-form');
  }

  onVariantChange() {
    this.updatePrice();
    this.updateState();
    this.updateLink();

    this.productForm.handleErrorMessage();
  }

  updatePrice() {
    if (!this.variantSelector) return;
    const selected = this.variantSelector.selectedOptions[0].dataset;
    const price = selected.variantPrice;
    const comparePrice = selected.variantCompareAtPrice || price;
    const unitPrice = selected.variantUnitPrice;

    if (price !== comparePrice) {
      this.querySelectorAll('.price-item--sale').forEach((el) => (el.innerHTML = price));
    }
    this.querySelectorAll('.price-item--regular').forEach((el) => (el.innerHTML = comparePrice));
    const unitPriceElement = this.querySelector('.price-item__unit');
    if (unitPriceElement) {
      unitPriceElement.innerHTML = unitPrice || '';
    }

    this.price?.classList.toggle('price--on-sale', price !== comparePrice);
  }

  updateLink() {
    if (!this.variantSelector) return;
    const selected = this.variantSelector.selectedOptions[0].dataset;
    this.querySelectorAll('a').forEach((el) => (el.href = atob(selected.variantUrl)));
  }

  updateState(newState = false) {
    if (!this.variantSelector) return;
    this.dataset.state = newState || this.variantSelector.selectedOptions[0].dataset.variantState;

    if (this.submitButton) {
      this.submitButton.ariaDisabled = this.dataset.state === 'sold-out';
    }
  }
}
customElements.define('card-product', CardProduct);

class CardProductSlider extends CardProduct {
  constructor() {
    super();
  }
}
customElements.define('card-product-slider', CardProductSlider);

class SiteMenu extends HTMLElement {
  constructor() {
    super();
    this.menuToggle = this.querySelector('#site-menu-toggle');
    this.details = this.querySelectorAll('details');
    this.summary = this.querySelectorAll('summary');
    this.submenusInput = this.querySelectorAll('input[type=checkbox]');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.summary.forEach((summary) => summary.addEventListener('click', this.onSummaryClick.bind(this)));
    this.menuToggle.addEventListener('change', this.onMenuToggle.bind(this));
  }

  onMenuToggle() {
    document.body.classList.toggle('overflow-hidden-desktop', this.menuToggle.checked);

    if (!this.menuToggle.checked) {
      setTimeout(() => {
        this.details.forEach((details) => {
          details.removeAttribute('open');
          details.classList.remove('menu-opening');
        });
        this.submenusInput.forEach((input) => (input.checked = false));
      }, 300);
    }
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    this.closeMegaMenu(event, this.summary);
  }

  onSummaryClick(event) {
    event.preventDefault();
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const isOpen = detailsElement.hasAttribute('open');

    if (isOpen) {
      detailsElement.classList.remove('menu-opening');
      summaryElement.setAttribute('aria-expanded', false);
      setTimeout(() => {
        detailsElement.removeAttribute('open');
        removeTrapFocus();
      }, 100);
      return;
    }

    trapFocus(detailsElement, summaryElement);

    // Close other details
    this.details.forEach((details) => {
      if (details === detailsElement) return;
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });

    const siteHeader = document.querySelector('.site-header');
    if (siteHeader) {
      const siteHeaderBottom = siteHeader.getBoundingClientRect().bottom;
      document.documentElement.style.setProperty('--header-bottom-position', `${siteHeaderBottom}px`);
    }
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);

    detailsElement.setAttribute('open', 'open');
    setTimeout(() => {
      detailsElement.classList.add('menu-opening');
      summaryElement.setAttribute('aria-expanded', true);
    }, 100);
  }
}
customElements.define('site-menu', SiteMenu);

class CartDrawerRecommendations extends HTMLElement {
  constructor() {
    super();

    this.recommendationsCache = {};
  }

  async connectedCallback() {
    if (!this.productId || window.innerWidth >= 750) {
      return;
    }

    if (!this.recommendationsCache[this.productId]) {
      this.recommendationsCache[this.productId] = fetch(
        `${window.routes.recommendations_url}?product_id=${this.productId}&limit=5&section_id=cart-recommendations`,
      );
    }

    const response = await this.recommendationsCache[this.productId];

    const div = document.createElement('div');

    div.innerHTML = await response.clone().text();

    const productRecommendationsElement = div.querySelector('cart-drawer-recommendations');

    if (productRecommendationsElement && productRecommendationsElement.hasChildNodes()) {
      this.innerHTML = productRecommendationsElement.innerHTML;
    }
  }

  get productId() {
    return this.getAttribute('product-id');
  }
}

window.customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);

class CartRecommendationsSlider extends HTMLElement {
  constructor() {
    super();
    this.recommendationsCache = {};
    this.cartItems = document.querySelector('.cart__items .js-contents');
    this.observer = undefined;
  }

  connectedCallback() {
    this.updateContent();
    this.observeCartUpdate();
  }

  async updateContent() {
    const recommendationsUrl = `${window.routes.recommendations_url}?product_id=${this.productId}&limit=10&section_id=${this.sectionId}`;

    if (!this.recommendationsCache[this.productId]) {
      this.recommendationsCache[this.productId] = fetch(recommendationsUrl);
    }

    const response = await this.recommendationsCache[this.productId];
    const div = document.createElement('div');
    div.innerHTML = await response.clone().text();

    const recommendationsContent = div.querySelector('.cart-recommendations-slider__list');
    if (recommendationsContent) {
      const targetList = this.querySelector('.cart-recommendations-slider__list');
      if (targetList) {
        targetList.innerHTML = recommendationsContent.innerHTML;
      }
    }
  }

  observeCartUpdate() {
    this.observer = new MutationObserver(() => {
      const table = this.cartItems.querySelector('table.cart-items');
      if (!table) return;
      const newId = table.getAttribute('first-product-id');
      if (newId && newId !== this.productId) {
        this.productId = newId;
        this.updateContent();
      }
    });

    this.observer.observe(this.cartItems, {
      childList: true,
      subtree: true,
    });
  }

  set productId(value) {
    this.setAttribute('product-id', value);
  }

  get productId() {
    return this.getAttribute('product-id');
  }

  get sectionId() {
    return this.getAttribute('section-id');
  }
}

window.customElements.define('cart-recommendations-slider', CartRecommendationsSlider);

class FreeShippingText extends HTMLElement {
  constructor() {
    super();
    /*     this.onCartUpdated = this.onCartUpdated.bind(this);
     */
  }

  /*   cartUpdateSubscription = undefined;

  connectedCallback() {
    this.cartUpdateSubscription = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdated);
  }

  disconnectedCallback() {
    if (this.cartUpdateSubscription) {
      this.cartUpdateSubscription();
    }
  }

  get threshold() {
    return parseFloat(this.getAttribute('threshold'));
  }

  onCartUpdated(data) {
    if (!data.cartData.items) {
      return;
    }
    const totalPrice = data.cartData.items
      .filter((item) => item.requires_shipping)
      .reduce((sum, item) => sum + item.final_line_price, 0);
    this.style.setProperty('--progress', Math.min(totalPrice / this.threshold, 1));
  } */
}
window.customElements.define('free-shipping-text', FreeShippingText);

/**
 * SwiperWrapper - Web component for sliders management
 * Encapsulates navigation logic for Swiper sliders
 */
class SwiperWrapper extends HTMLElement {
  constructor() {
    super();
    this._initialized = false;
    this._debug = false;
    this._boundHandleClick = this._handleClick.bind(this);
  }

  connectedCallback() {
    this._init();
    this._addEventListeners();
    this._log('Component initialized');
  }

  disconnectedCallback() {
    this._removeEventListeners();
    this._log('Event listeners cleaned up');
  }

  /**
   * Initializes component element references
   * @private
   */
  _init() {
    this.swiperContainer = this.querySelector('swiper-container');
    if (!this.swiperContainer) {
      console.warn('SwiperWrapper: No swiper-container element found');
      return;
    }

    this.navigationArrows = this.querySelectorAll('.swiper-wrapper__arrow');
    if (this.navigationArrows.length === 0) {
      console.warn('SwiperWrapper: No navigation arrows found');
    }

    this._initialized = true;
  }

  /**
   * Handles slider navigation
   * @param {string} direction - 'prev' or 'next'
   * @private
   */
  _navigate(direction) {
    if (!this._initialized || !this.swiperContainer?.swiper) {
      this._log('Swiper is not initialized');
      return;
    }

    const swiper = this.swiperContainer.swiper;
    if (direction === 'prev') {
      swiper.slidePrev();
    } else {
      swiper.slideNext();
    }

    this._log(`Navigation ${direction} executed`);
  }

  /**
   * Event handler for arrow clicks
   * @param {Event} event - Click event
   * @private
   */
  _handleClick(event) {
    const arrow = event.target.closest('.swiper-wrapper__arrow');
    if (!arrow) return;

    event.preventDefault();
    const direction = arrow.classList.contains('--prev') ? 'prev' : 'next';
    this._navigate(direction);
  }

  /**
   * Adds event listeners
   * @private
   */
  _addEventListeners() {
    // Using event delegation for better performance
    this.addEventListener('click', this._boundHandleClick);
  }

  /**
   * Removes event listeners
   * @private
   */
  _removeEventListeners() {
    this.removeEventListener('click', this._boundHandleClick);
  }

  /**
   * Displays debug messages if enabled
   * @param {string} message - Message to display
   * @private
   */
  _log(message) {
    if (this._debug) {
      console.log(`[SwiperWrapper] ${message}`);
    }
  }
}
customElements.define('swiper-wrapper', SwiperWrapper);

// Clean pagination URLs for SEO
function cleanPaginationSEO() {
  // Helper function to clean page=1 from URLs
  function cleanPageUrl(url) {
    if (!url) return url;

    const urlObj = new URL(url);
    if (urlObj.searchParams.get('page') === '1') {
      urlObj.searchParams.delete('page');
    }
    return urlObj.toString();
  }

  const head = document.head;

  // Clean canonical URL if it contains page=1
  const canonical = head.querySelector('link[rel="canonical"]');
  if (canonical && canonical.href.includes('page=1')) {
    canonical.href = cleanPageUrl(canonical.href);
  }

  // Clean prev link
  const prevLink = head.querySelector('link[rel="prev"]');
  if (prevLink && prevLink.href.includes('page=1')) {
    prevLink.href = cleanPageUrl(prevLink.href);
  }
}

// Run on DOM content loaded
document.addEventListener('DOMContentLoaded', cleanPaginationSEO);

/**
 * Initialize Klaviyo form link handlers
 * Allows opening Klaviyo popup forms via links with href="#form-[formId]"
 * Tracks the source URL (page where form was opened) for analytics
 */
function initKlaviyoFormLinks() {
  // Function to track form source URL in Klaviyo profile
  function trackFormSourceInProfile(sourceUrl, formId = null) {
    if (typeof window._learnq !== 'undefined' && Array.isArray(window._learnq)) {
      const identifyData = {
        'Source URL': sourceUrl,
        'Source Page': window.location.pathname,
      };

      if (formId) {
        identifyData['Last Form Opened'] = formId;
        identifyData['Last Form Opened Date'] = new Date().toISOString();
      }

      // Use identify to store Source URL in the customer profile
      window._learnq.push(['identify', identifyData]);
    }
  }

  // Function to track form open source
  function trackFormSource(formId, sourceUrl) {
    // Store source URL in Klaviyo profile using identify API
    trackFormSourceInProfile(sourceUrl, formId);

    // Also track as an event for analytics
    if (typeof window._learnq !== 'undefined' && Array.isArray(window._learnq)) {
      window._learnq.push([
        'track',
        'Opened Form',
        {
          'Form ID': formId,
          'Source URL': sourceUrl,
          'Source Page': window.location.pathname,
        },
      ]);
    }

    // Also try to set a hidden field in the form after it opens
    // This will run after a short delay to allow the form to render
    setTimeout(() => {
      const form = document.querySelector(`.klaviyo-form-${formId}, [data-form-id="${formId}"]`);
      if (form) {
        // Try common hidden field names
        const hiddenFieldNames = ['source_url', 'page_url', 'form_source', 'referrer_url'];
        hiddenFieldNames.forEach((fieldName) => {
          const hiddenField = form.querySelector(
            `input[name="${fieldName}"], input[type="hidden"][name*="source"], input[type="hidden"][name*="url"]`,
          );
          if (hiddenField) {
            hiddenField.value = sourceUrl;
          }
        });
      }
    }, 500);
  }

  // Function to open Klaviyo form with source tracking
  function openKlaviyoForm(formId, sourceUrl) {
    // Check if Klaviyo is loaded
    if (typeof window._klOnsite !== 'undefined' && Array.isArray(window._klOnsite)) {
      // Track the source before opening
      if (sourceUrl) {
        trackFormSource(formId, sourceUrl);
      }

      window._klOnsite.push(['openForm', formId]);
      return true;
    }
    return false;
  }

  // Function to wait for Klaviyo to load and then open form
  function waitForKlaviyoAndOpen(formId, sourceUrl, maxAttempts = 50, attempt = 0) {
    if (openKlaviyoForm(formId, sourceUrl)) {
      return;
    }

    if (attempt < maxAttempts) {
      setTimeout(() => {
        waitForKlaviyoAndOpen(formId, sourceUrl, maxAttempts, attempt + 1);
      }, 100);
    } else {
      console.warn(`Klaviyo form ${formId} could not be opened: Klaviyo script not loaded`);
    }
  }

  // Handle clicks on links with #form- pattern
  function handleKlaviyoFormLink(event) {
    const link = event.target.closest('a[href^="#form-"]');
    if (!link) return;

    const href = link.getAttribute('href');
    const match = href.match(/^#form-(.+)$/);

    if (match && match[1]) {
      event.preventDefault();
      const formId = match[1];
      const sourceUrl = window.location.href;

      // Try to open immediately, or wait for Klaviyo to load
      if (!openKlaviyoForm(formId, sourceUrl)) {
        waitForKlaviyoAndOpen(formId, sourceUrl);
      }
    }
  }

  // Use event delegation to handle dynamically added links
  document.addEventListener('click', handleKlaviyoFormLink);

  // Also handle hash changes in case user navigates directly to #form-[id]
  function handleHashChange() {
    const hash = window.location.hash;
    const match = hash.match(/^#form-(.+)$/);

    if (match && match[1]) {
      const formId = match[1];
      const sourceUrl = window.location.href;
      if (!openKlaviyoForm(formId, sourceUrl)) {
        waitForKlaviyoAndOpen(formId, sourceUrl);
      }
    }
  }

  // Check hash on page load
  if (window.location.hash.match(/^#form-/)) {
    handleHashChange();
  }

  // Listen for hash changes
  window.addEventListener('hashchange', handleHashChange);

  // Listen for Klaviyo form events to track source URL
  // This handles cases where forms open automatically or are submitted (not via our script)
  window.addEventListener('klaviyoForms', function (e) {
    if (e.detail.type === 'submit') {
      const sourceUrl = window.location.href;
      const formId = e.detail.formId || e.detail.meta?.formId || null;

      // Track source URL in profile
      trackFormSourceInProfile(sourceUrl, formId);

      // Also track as an event
      if (typeof window._learnq !== 'undefined' && Array.isArray(window._learnq)) {
        window._learnq.push([
          'track',
          'Submitted Form',
          {
            'Form ID': formId || 'unknown',
            'Source URL': sourceUrl,
            'Source Page': window.location.pathname,
          },
        ]);
      }
    } else if (e.detail.type === 'open') {
      // Track when form opens automatically
      const sourceUrl = window.location.href;
      const formId = e.detail.formId || e.detail.meta?.formId || null;

      // Track source URL in profile
      trackFormSourceInProfile(sourceUrl, formId);
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initKlaviyoFormLinks);
} else {
  initKlaviyoFormLinks();
}

/**
 * LocalizationDialog - Custom element for country and language selection popup
 * Manages the dialog opening/closing, country search, language filtering, and form submission
 */
class LocalizationDialog extends HTMLElement {
  constructor() {
    super();
    this.dialog = this.querySelector('[data-localization-dialog]');
    this.closeButton = this.querySelector('[data-localization-close]');
    this.countryDisplayButton = this.querySelector('[data-localization-country-display-button]');
    this.searchInput = this.querySelector('[data-localization-search]');
    this.countryList = this.querySelector('[data-localization-country-list]');
    this.countryButtons = this.querySelectorAll('[data-localization-country-button]');
    this.languageList = this.querySelector('[data-localization-language-list]');
    this.languageButtons = this.querySelectorAll('[data-localization-language-button]');
    this.countryInput = this.querySelector('[data-localization-country-input]');
    this.languageInput = this.querySelector('[data-localization-language-input]');
    this.form = this.querySelector('form');
    this.selectedCountryCode = this.countryInput?.value || null;
    this.isSearchMode = false;
  }

  connectedCallback() {
    if (!this.dialog) return;

    // Find all trigger buttons on the page and attach click handlers
    const triggerButtons = document.querySelectorAll('[data-localization-trigger]');
    triggerButtons.forEach((button) => {
      button.addEventListener('click', this.open.bind(this));
    });

    // Close dialog on close button click
    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.close.bind(this));
    }

    // Close dialog on backdrop click
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) {
        this.close();
      }
    });

    // Close dialog on ESC key
    this.dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.close();
      }
    });

    // Country display button click - transform to search input
    if (this.countryDisplayButton) {
      this.countryDisplayButton.addEventListener('click', this.activateSearchMode.bind(this));
    }

    // Country search functionality
    if (this.searchInput) {
      this.searchInput.addEventListener('input', this.handleSearch.bind(this));
      this.searchInput.addEventListener('keydown', this.handleSearchKeydown.bind(this));
      this.searchInput.addEventListener('blur', (event) => {
        // Delay to allow click on country button to register
        setTimeout(() => {
          if (!this.countryList?.contains(document.activeElement)) {
            this.deactivateSearchMode();
          }
        }, 200);
      });
    }

    // Close dropdown when clicking outside
    document.addEventListener('click', (event) => {
      const searchWrapper = this.querySelector('[data-localization-search-wrapper]');
      if (searchWrapper && !searchWrapper.contains(event.target)) {
        if (this.isSearchMode) {
          this.deactivateSearchMode();
        }
        this.hideCountryList();
      }
    });

    // Country selection
    this.countryButtons.forEach((button) => {
      button.addEventListener('click', this.handleCountrySelect.bind(this));
    });

    // Language selection
    this.languageButtons.forEach((button) => {
      button.addEventListener('click', this.handleLanguageSelect.bind(this));
    });

    // Form submission
    if (this.form) {
      this.form.addEventListener('submit', this.handleSubmit.bind(this));
    }
  }

  open() {
    if (!this.dialog) return;
    this.dialog.showModal();
    document.body.classList.add('overflow-hidden');

    // Reset to display mode when opening
    this.deactivateSearchMode();

    // Trap focus within dialog
    trapFocus(this.dialog, this.closeButton);
  }

  close() {
    if (!this.dialog) return;
    this.dialog.close();
    document.body.classList.remove('overflow-hidden');

    // Find the trigger button that opened the dialog (if any)
    const triggerButtons = document.querySelectorAll('[data-localization-trigger]');
    removeTrapFocus(triggerButtons[0] || null);

    // Reset search and display mode
    this.deactivateSearchMode();
    if (this.searchInput) {
      this.searchInput.value = '';
    }
    this.hideCountryList();
  }

  activateSearchMode() {
    if (!this.countryDisplayButton || !this.searchInput) return;

    this.isSearchMode = true;
    this.countryDisplayButton.classList.add('localization-popup__country-display-button--hidden');
    this.searchInput.classList.remove('localization-popup__search--hidden');
    this.searchInput.value = '';
    this.searchInput.focus();
    this.showCountryList();
    this.handleSearch({ target: this.searchInput });
  }

  deactivateSearchMode() {
    if (!this.countryDisplayButton || !this.searchInput) return;

    this.isSearchMode = false;
    this.countryDisplayButton.classList.remove('localization-popup__country-display-button--hidden');
    this.searchInput.classList.add('localization-popup__search--hidden');
    this.searchInput.value = '';
    this.hideCountryList();

    // Update display button text with current selection
    const selectedButton = Array.from(this.countryButtons).find((btn) => btn.getAttribute('aria-current') === 'true');
    if (selectedButton && this.countryDisplayButton) {
      this.countryDisplayButton.textContent = selectedButton.textContent.trim();
    }
  }

  showCountryList() {
    if (this.countryList) {
      this.countryList.hidden = false;
    }
  }

  hideCountryList() {
    if (this.countryList) {
      this.countryList.hidden = true;
    }
  }

  handleSearch(event) {
    const searchTerm = event.target.value.toLowerCase().trim();
    const countryItems = this.countryList?.querySelectorAll('.localization-popup__country-item') || [];

    // Show list when typing
    if (searchTerm || this.isSearchMode) {
      this.showCountryList();
    }

    countryItems.forEach((item) => {
      const button = item.querySelector('[data-localization-country-button]');
      if (!button) return;

      const countryName = button.getAttribute('data-country-name')?.toLowerCase() || '';
      const countryCode = button.getAttribute('data-country-code')?.toLowerCase() || '';
      const countryDisplayText = button.textContent.trim().toLowerCase();
      const matches =
        !searchTerm ||
        countryName.includes(searchTerm) ||
        countryCode.includes(searchTerm) ||
        countryDisplayText.includes(searchTerm);

      item.style.display = matches ? '' : 'none';
    });
  }

  handleSearchKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      const visibleItems = Array.from(
        this.countryList?.querySelectorAll('.localization-popup__country-item') || [],
      ).filter((item) => item.style.display !== 'none');

      if (visibleItems.length > 0) {
        const firstButton = visibleItems[0].querySelector('[data-localization-country-button]');
        if (firstButton) {
          this.handleCountrySelect({ currentTarget: firstButton });
          // Submit form after selection
          if (this.form) {
            this.form.submit();
          }
        }
      }
    }
  }

  handleCountrySelect(event) {
    const button = event.currentTarget;
    const countryCode = button.getAttribute('data-country-code');
    const availableLanguages = button.getAttribute('data-available-languages')?.split(',') || [];

    if (!countryCode) return;

    // Update selected country
    this.selectedCountryCode = countryCode;
    if (this.countryInput) {
      this.countryInput.value = countryCode;
    }

    // Update visual selection
    this.countryButtons.forEach((btn) => {
      btn.removeAttribute('aria-current');
    });
    button.setAttribute('aria-current', 'true');

    // Update display button text
    if (this.countryDisplayButton) {
      this.countryDisplayButton.textContent = button.textContent.trim();
    }

    // Hide dropdown list and deactivate search mode
    this.hideCountryList();
    this.deactivateSearchMode();

    // Update available languages
    this.updateLanguages(availableLanguages);
  }

  updateLanguages(availableLanguageCodes) {
    const languageItems = this.languageList?.querySelectorAll('.localization-popup__language-item') || [];

    languageItems.forEach((item) => {
      const languageCode = item.getAttribute('data-language-code');
      const isAvailable = availableLanguageCodes.includes(languageCode);

      // Show/hide language based on availability
      item.hidden = !isAvailable;

      // If current language is not available, select first available language
      if (!isAvailable && this.languageInput?.value === languageCode) {
        const firstAvailable = languageItems.find((li) => {
          const code = li.getAttribute('data-language-code');
          return availableLanguageCodes.includes(code);
        });

        if (firstAvailable) {
          const firstLanguageCode = firstAvailable.getAttribute('data-language-code');
          if (this.languageInput) {
            this.languageInput.value = firstLanguageCode;
          }

          // Update visual selection
          this.languageButtons.forEach((btn) => {
            btn.removeAttribute('aria-current');
            if (btn.getAttribute('data-language-code') === firstLanguageCode) {
              btn.setAttribute('aria-current', 'true');
            }
          });
        }
      }
    });
  }

  handleLanguageSelect(event) {
    const button = event.currentTarget;
    const languageCode = button.getAttribute('data-language-code');

    if (!languageCode) return;

    // Update selected language
    if (this.languageInput) {
      this.languageInput.value = languageCode;
    }

    // Update visual selection
    this.languageButtons.forEach((btn) => {
      btn.removeAttribute('aria-current');
    });
    button.setAttribute('aria-current', 'true');
  }

  handleSubmit(event) {
    // Form will submit normally, causing a page reload with new localization
    // No need to prevent default
  }
}

customElements.define('localization-dialog', LocalizationDialog);

class ObfuscatedLink extends HTMLElement {
  constructor() {
    super();
    this.linkCreated = false;
  }

  showLink() {
    if (this.linkCreated) return;

    const href = atob(this.getAttribute('data-href'));
    const link = document.createElement('a');
    link.href = href;

    // Copy all relevant attributes from the custom element to the link
    if (this.hasAttribute('data-target')) {
      link.target = this.getAttribute('data-target');
    }
    if (this.hasAttribute('data-rel')) {
      link.rel = this.getAttribute('data-rel');
    }

    // Move all children into the link
    while (this.firstChild) {
      link.appendChild(this.firstChild);
    }

    // Add the link to the custom element
    this.appendChild(link);
    this.linkCreated = true;

    // Remove the tabindex now that the link is created
    this.removeAttribute('tabindex');

    // If the element has focus, transfer it to the link
    if (document.activeElement === this) {
      link.focus();
    }
  }

  connectedCallback() {
    // Make the element focusable for keyboard navigation
    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '0');
    }

    this.addEventListener('mouseover', this.showLink.bind(this));
    this.addEventListener('focus', this.showLink.bind(this));
  }

  disconnectedCallback() {
    this.removeEventListener('mouseover', this.showLink.bind(this));
    this.removeEventListener('focus', this.showLink.bind(this));
  }
}
customElements.define('obfuscated-link', ObfuscatedLink);
